import { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import debug from 'debug';

import ItemTypeIcon from 'components/common/item-type-icon';
import { checkItemTypeHierarchy, getItemTypeList, inWorkspace } from 'components/item-move/selectWorkspaceAndItemType';
import { classnames } from 'lib/classnames';
import fetch from 'lib/fetch';
import useI18n from 'lib/hooks/useI18n';
import { ResultType, WorkflowDataProps } from 'lib/types/workflow';

import WorkspaceSelect from '../../../item-move/WorkspaceSelect';

import styles from './index.module.less';

const cx = classnames(styles);
export type PluginsType = {
  man_hour_plugin?: string[]; // 工时
} & Record<string, string[]>;

interface SelectWorkspaceAndItemTypeProps {
  items: any[];
  title?: string;
  description?: string;
  onChange?: (changeData: {
    workspaceData?: Parse.Attributes;
    itemTypeData?: { key: string; value: string };
    securityLevelIsChange?: boolean;
  }) => void;
  onWorkflowChange?: (workflowData: WorkflowDataProps & Parse.Attributes) => void;
  updateSecurityChange?: (result: boolean) => void;
  updateWorkflowPlugin?: (pluginsType: PluginsType) => void;
  updateErrorMessage: (result: ResultType) => void;
}

const log = debug('selectWorkspaceAndItemType:content');

const DEFAULT_FLOW_MESSAGE: ResultType = { result: false, message: '' };
const SelectWorkspaceAndItemType: React.FC<SelectWorkspaceAndItemTypeProps> = ({
  items,
  title,
  description,
  onChange,
  onWorkflowChange,
  // updateWorkflowPlugin,
  updateErrorMessage,
}) => {
  const { t } = useI18n();
  const [workspacePlugins, setWorkspacePlugins] = useState<Record<string, PluginsType>>({});
  const [itemTypeList, setItemTypeList] = useState([]);
  const [selectItemType, setSelectItemType] = useState();
  const [selectLoading, setSelectLoading] = useState(false);
  const [selectDetail, setSelectDetail] = useState(undefined); // 当前选择事项的空间信息
  const [itemParentAndChildren, setItemParentAndChildren] = useState(undefined);

  const fetchParentAndChildren = useCallback(async (ids: string[]) => {
    try {
      if (!ids?.length) return;
      // 查询当前空间可用插件的类型
      const data = await fetch.$post(`items/parentAndChildren`, { ids });
      log('parentAndChildren', data, ids);
      setItemParentAndChildren(data);
    } catch (error) {
      console.error(error.message);
    }
  }, []);

  useEffect(() => {
    fetchParentAndChildren(items.map(item => item.objectId));
  }, [fetchParentAndChildren, items]);

  const moveItemDetail = useMemo(() => {
    // 计算当前批量迁移的事项
    const moveData = [];
    items.forEach(item => {
      const moveWorkspace = item.workspace.objectId;
      const moveItemType = item.itemType.objectId;
      if (
        !moveData.find(md => md.itemType === moveItemType && md.workspace === moveWorkspace && item.objectId === md.id)
      ) {
        moveData.push({ id: item.objectId, workspace: moveWorkspace, itemType: moveItemType });
      }
    });
    return moveData;
  }, [items]);

  // 判断当前迁移是否符合层级关系，在空间切换和类型切换调用
  const checkItemHierarchy = useCallback(
    ({ itemTypeValue, workspaceDetail }) => {
      updateErrorMessage(DEFAULT_FLOW_MESSAGE);
      const selectItemTypeValue = itemTypeValue || selectItemType; // 迁移的目标事项类型
      const selectWorkspace = workspaceDetail || selectDetail; // 迁移的目标空间
      log('current select hierarchy', selectItemTypeValue, selectWorkspace);
      if (!selectItemTypeValue || !selectWorkspace) return;

      for (const moveData of moveItemDetail) {
        // 查找出不包含工时类型的子事项
        // 这个应该看当前迁移出的事项的空间
        const manHourItemTypes = workspacePlugins[moveData.workspace]?.man_hour_plugin || [];
        const items = itemParentAndChildren[moveData.id].children.filter(
          item => !manHourItemTypes.includes(item.itemType.key),
        );
        // 查找出子事项是否是同空间还是不同空间
        const sameWorkspaceItems = items.filter(item => item.workspace.objectId === moveData.workspace);
        log('same workspace children', sameWorkspaceItems);
        // 如果不相等，说明是迁移空间
        if (moveData.workspace !== selectWorkspace.objectId) {
          if (sameWorkspaceItems.length) {
            log(
              'condition',
              moveData.workspace,
              selectWorkspace.objectId,
              sameWorkspaceItems,
              itemParentAndChildren[moveData.id].children,
            );
            // 如果存在子事项是当前空间的，则不允许迁移
            updateErrorMessage({ result: false, message: t('pages.itemMove.hierarchyTip1') });
            return;
          }
        }

        // 检查层级
        const checkHierarchy = (
          selectWorkspaceDetail: Parse.Attributes,
          isParent: boolean,
          children?: Parse.Attributes[],
        ): boolean => {
          const checkValue = isParent ? { parent: itemParentAndChildren[moveData.id].parent } : { children };
          const matchChildrenHierarchy = checkItemTypeHierarchy(selectWorkspaceDetail, selectItemTypeValue, checkValue);
          if (!matchChildrenHierarchy) {
            log('check result', checkValue, selectWorkspaceDetail, selectItemTypeValue);
            updateErrorMessage({
              result: false,
              message: isParent ? t(`pages.itemMove.hierarchyTip3`) : t(`pages.itemMove.hierarchyTip2`),
            });
            return false;
          }
          return true;
        };

        // 如果相等，说明是在同一空间内修改类型
        // 如果当前事项有同空间的子事项，需要判断层级管理
        if (sameWorkspaceItems.length && !checkHierarchy(moveData.workspace, false, sameWorkspaceItems)) {
          return;
        }
        // 如果当前事项有同空间的父事项，需要判断层级管理
        if (
          itemParentAndChildren[moveData.id].parent &&
          itemParentAndChildren[moveData.id].parent.workspace.objectId === moveData.workspace &&
          moveData.workspace === selectWorkspace.objectId &&
          !checkHierarchy(selectWorkspace, true)
        ) {
          return;
        }

        // 如果是不同空间，迁移目前的父/子事项都在迁移空间
        if (
          moveData.workspace !== selectWorkspace.objectId &&
          inWorkspace(
            selectWorkspace.objectId,
            itemParentAndChildren[moveData.id].parent,
            itemParentAndChildren[moveData.id].children,
          )
        ) {
          log('not equal workspace condition', moveData.workspace, selectWorkspace.objectId);
          if (itemParentAndChildren[moveData.id].children && !checkHierarchy(selectWorkspace.objectId, true)) {
            return;
          }
          const _checkHierarchy = () => {
            return (
              itemParentAndChildren[moveData.id].children?.length &&
              !checkHierarchy(selectWorkspace, false, itemParentAndChildren[moveData.id].children)
            );
          };
          if (_checkHierarchy()) {
            return;
          }
        }
      }
    },
    [updateErrorMessage, selectItemType, selectDetail, moveItemDetail, workspacePlugins, itemParentAndChildren, t],
  );

  const fetchWorkflow = useCallback(
    async (workspaceId: string, itemTypeId: string, workflowSchemeId: string) => {
      log('fetch workflow', workflowSchemeId, itemTypeId, workspaceId);
      if (!workflowSchemeId || !itemTypeId || !workspaceId) return;
      try {
        setSelectLoading(true);
        const workflowData = await fetch.$post(`/workflows/workspace/itemType`, {
          itemType: itemTypeId,
          workflowScheme: workflowSchemeId,
          workspace: workspaceId,
        });
        onWorkflowChange?.(workflowData);
      } catch (error) {
        console.error(error.message);
      } finally {
        setSelectLoading(false);
      }
    },
    [onWorkflowChange],
  );

  const itemTypeChange = useCallback(
    async (value, data) => {
      log('itemType change', selectDetail, value, data);
      await fetchWorkflow(selectDetail.objectId, value, selectDetail.workflowScheme?.objectId);
      setSelectItemType(value);
      checkItemHierarchy({ itemTypeValue: value, workspaceDetail: undefined });
      onChange({ itemTypeData: data });
    },
    [selectDetail, fetchWorkflow, checkItemHierarchy, onChange],
  );

  // 查询空间的插件
  const fetchWorkspacePlugin = useCallback(
    async ({ itemTypes, workspaceData }) => {
      const itemTypeData = itemTypes || itemTypeList;
      const selectWorkspaceId = workspaceData.objectId;
      log('fetch workspace plugin', selectWorkspaceId, itemTypeData);
      if (!selectWorkspaceId) return;
      try {
        setSelectLoading(true);
        // 查询当前空间可用插件的类型
        const data = await fetch.$get(`/itemType/apps/workspace/${workspaceData.key}`);
        log('workspace plugin data', data, itemTypeList);
        workspacePlugins[selectWorkspaceId] = data;
        setWorkspacePlugins(workspacePlugins);
        // if (selectWorkspaceId === workspaceId) {
        //   updateWorkflowPlugin && updateWorkflowPlugin(data);
        // }
      } catch (error) {
        console.error(error.message);
      } finally {
        setSelectLoading(false);
      }
    },
    [itemTypeList, workspacePlugins],
  );

  const workspaceChange = useCallback(
    async (data: Parse.Attributes) => {
      log('workspace change', selectDetail, data);
      if (!data?.objectId) return;
      setSelectDetail(data);

      // 只能选第一层的条件限制
      // 1、如果是不同空间的迁移，并且当前事项有同空间的父事项，这种情况只能选择目标空间的第一层
      // 2、如果是相同空间的迁移，并且有父事项和当前空间不一样
      let isAllNotFirstHierarchy = true; // 是否所有都是包含子层级
      for (const item of items) {
        let isFirstHierarchy = true;
        if (data.objectId === item.workspace.objectId) {
          if (
            itemParentAndChildren[item.objectId]?.parent &&
            itemParentAndChildren[item.objectId].parent?.workspace?.objectId === data.objectId
          ) {
            isFirstHierarchy = false;
          }
        } else {
          // 如果是不同空间，判断一下原来事项的父子事项是否和迁移目标空间是否一致
          if (
            inWorkspace(
              data.objectId,
              itemParentAndChildren[item.objectId]?.parent,
              itemParentAndChildren[item.objectId]?.children,
            )
          ) {
            // 这种情况可以选择，目标空间的子级类型，但需要判断层级关系
            isFirstHierarchy = false;
          }
        }
        if (isFirstHierarchy) {
          isAllNotFirstHierarchy = false;
          break;
        }
      }

      const itemTypeList = await getItemTypeList(data, !isAllNotFirstHierarchy);
      setItemTypeList(itemTypeList);
      setSelectItemType(undefined);
      const changeItemType = { key: '', value: '', icon: '' };
      await fetchWorkflow(data.objectId, selectItemType, data.workflowScheme?.objectId);
      await fetchWorkspacePlugin({ workspaceData: data, itemTypes: itemTypeList });
      onChange({
        workspaceData: data,
        itemTypeData: changeItemType,
      });
      // 空间变更不做校验
      // 检查层级处理
      checkItemHierarchy({ workspaceDetail: data, itemTypeValue: undefined });
    },
    [
      selectDetail,
      fetchWorkflow,
      selectItemType,
      fetchWorkspacePlugin,
      onChange,
      checkItemHierarchy,
      items,
      itemParentAndChildren,
    ],
  );

  return (
    <div className={cx('select-wrap')}>
      {title && <div className={cx('title')}>{title}</div>}
      {description && <div className={cx('description')}>{description}</div>}
      <div className={cx('select-item', 'select-item-header')}>
        <div className={cx('select-label')}>{t('pages.itemMove.targetWorkspace')}</div>
        <WorkspaceSelect onWorkspaceChange={workspaceChange} />
      </div>
      <div className={cx('select-item')}>
        <div className={cx('select-label')}>{t('pages.itemMove.targetItemType')}</div>
        <Select
          loading={selectLoading}
          className={cx('select-item-content')}
          showSearch
          filterOption={(input, option) => {
            const value = (option?.children?.[1] || '').trim().toLowerCase();
            return value.includes(input.trim().toLowerCase());
          }}
          placeholder={t('pages.itemMove.selectItemType')}
          value={selectItemType}
          onChange={itemTypeChange}
        >
          {itemTypeList.map(ele => {
            return (
              <Select.Option key={ele.label} value={ele.value} icon={ele.icon}>
                <ItemTypeIcon name={ele.name} icon={ele.icon} className={cx('item-type-icon')} />
                {ele.label}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    </div>
  );
};

export default SelectWorkspaceAndItemType;
